import React, { useEffect, useRef, useState } from 'react';
import { Segment, TrackPoint } from '../types';
import L from 'leaflet';
import { ChevronLeft, ChevronRight, Layers, Clock, MapPin, Zap, Heart, Scissors, RotateCcw, X, Settings2 } from 'lucide-react';

interface MapVisualizationProps {
  trackPoints: TrackPoint[];
  segments: Segment[];
  selectedSegmentId: number | null;
  onSelectSegment: (id: number | null) => void;
  minSpeed: number;
  maxSpeed: number;
  hideBackground?: boolean;
  onTrimStart?: (id: number) => void;
  onTrimEnd?: (id: number) => void;
  onTrimReset?: (id: number) => void;
}

export const MapVisualization: React.FC<MapVisualizationProps> = ({ 
  trackPoints, 
  segments, 
  selectedSegmentId, 
  onSelectSegment,
  minSpeed,
  maxSpeed,
  hideBackground = false,
  onTrimStart,
  onTrimEnd,
  onTrimReset
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const layersRef = useRef<L.LayerGroup | null>(null);
  const [showTrimControls, setShowTrimControls] = useState(false);

  // Reset trim menu visibility when selection changes
  useEffect(() => {
    setShowTrimControls(false);
  }, [selectedSegmentId]);

  // Continuous Color Scale Helper
  const getSpeedColor = (speed: number) => {
    const effectiveMax = Math.max(maxSpeed, minSpeed + 5);
    const t = Math.max(0, Math.min(1, (speed - minSpeed) / (effectiveMax - minSpeed)));
    // HSL fade from Blue (240) to Red (0)
    const hue = (1 - t) * 240; 
    return `hsl(${hue}, 100%, 50%)`;
  };

  // Derive data for UI overlays
  const selectedSegment = selectedSegmentId ? segments.find(s => s.id === selectedSegmentId) : null;
  const currentIndex = selectedSegmentId ? segments.findIndex(s => s.id === selectedSegmentId) : -1;

  const handlePrev = () => {
    if (currentIndex > 0) {
      onSelectSegment(segments[currentIndex - 1].id);
    } else if (currentIndex === -1 && segments.length > 0) {
      onSelectSegment(segments[segments.length - 1].id);
    }
  };

  const handleNext = () => {
    if (currentIndex !== -1 && currentIndex < segments.length - 1) {
      onSelectSegment(segments[currentIndex + 1].id);
    } else if (currentIndex === -1 && segments.length > 0) {
      onSelectSegment(segments[0].id);
    }
  };

  const handleAll = () => {
    onSelectSegment(null);
  };

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (!mapContainerRef.current) return;

    // Initialize map
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapContainerRef.current, { zoomControl: false }).setView([0, 0], 13);
      L.control.zoom({ position: 'bottomright' }).addTo(mapInstanceRef.current);
      
      L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        attribution: 'Tiles &copy; Esri'
      }).addTo(mapInstanceRef.current);
      
      layersRef.current = L.layerGroup().addTo(mapInstanceRef.current);
      
      mapInstanceRef.current.on('click', () => {
        onSelectSegment(null);
      });
    }

    const map = mapInstanceRef.current;
    const layerGroup = layersRef.current;

    if (layerGroup) {
      layerGroup.clearLayers();
    }

    if (trackPoints.length > 0 && layerGroup) {
      const allLatLngs = trackPoints.map(pt => [pt.lat, pt.lon] as [number, number]);
      
      const shouldDrawBackground = selectedSegmentId !== null;

      if (shouldDrawBackground) {
        L.polyline(allLatLngs, {
          color: '#94a3b8',
          weight: 1,
          opacity: 0.2,
          interactive: false
        }).addTo(layerGroup);
      }

      segments.forEach(seg => {
        const isSelected = seg.id === selectedSegmentId;
        const hasSelection = selectedSegmentId !== null;
        
        let opacity = 1;
        let weight = 4;
        
        if (hasSelection) {
          if (isSelected) {
            opacity = 1;
            weight = 6;
          } else {
            opacity = 0.1;
            weight = 2;
          }
        } else {
          opacity = 0.9;
        }

        const segmentGroup = L.featureGroup().addTo(layerGroup);

        for (let i = 0; i < seg.points.length - 1; i++) {
          const p1 = seg.points[i];
          const p2 = seg.points[i+1];
          const segmentSpeed = (p1.speed + p2.speed) / 2;
          const color = getSpeedColor(segmentSpeed);

          L.polyline([[p1.lat, p1.lon], [p2.lat, p2.lon]], {
            color: color,
            weight: weight,
            opacity: opacity,
            interactive: false 
          }).addTo(segmentGroup);
        }

        const hitLatLngs = seg.points.map(pt => [pt.lat, pt.lon] as [number, number]);
        const hitPolyline = L.polyline(hitLatLngs, {
          color: '#ffffff', 
          opacity: 0.0,     
          weight: 25,      
        });

        hitPolyline.bindTooltip("", { 
             sticky: true, 
             direction: 'top',
             className: 'bg-slate-900/90 text-white border border-slate-700 px-2 py-1 rounded text-xs shadow-xl backdrop-blur-md'
        });

        hitPolyline.on('mousemove', (e: L.LeafletMouseEvent) => {
          let minDist = Infinity;
          let closestPoint = seg.points[0];

          seg.points.forEach(pt => {
            const dist = map.distance(e.latlng, [pt.lat, pt.lon]);
            if (dist < minDist) {
              minDist = dist;
              closestPoint = pt;
            }
          });

          const hrText = closestPoint.heartRate ? ` | HR: ${closestPoint.heartRate}` : '';
          const content = `<b>${closestPoint.speed.toFixed(1)} km/h</b>${hrText}`;
          
          hitPolyline.setTooltipContent(content);
        });

        hitPolyline.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          onSelectSegment(seg.id);
        });

        hitPolyline.addTo(segmentGroup);

        if (isSelected) {
          segmentGroup.bringToFront();
        }
      });

      if (selectedSegmentId) {
        const selectedSeg = segments.find(s => s.id === selectedSegmentId);
        if (selectedSeg) {
           const bounds = L.latLngBounds(selectedSeg.points.map(p => [p.lat, p.lon]));
           map.flyToBounds(bounds, { padding: [100, 100], maxZoom: 19, animate: true, duration: 0.8 });
        }
      } else if (segments.length > 0) {
         const allSegPoints = segments.flatMap(s => s.points);
         if (allSegPoints.length > 0 && (map.getCenter().lat === 0 || !selectedSegmentId)) {
             const bounds = L.latLngBounds(allSegPoints.map(p => [p.lat, p.lon]));
             map.fitBounds(bounds, { padding: [50, 50] });
         }
      }
    }
  }, [trackPoints, segments, selectedSegmentId, minSpeed, maxSpeed, hideBackground]);

  return (
    <div className="w-full h-[500px] bg-slate-900 rounded-2xl overflow-hidden border border-slate-800 shadow-inner relative z-0 group">
      <div ref={mapContainerRef} className="w-full h-full" />
      
      {/* Mobile Navigation Controls */}
      <div className="absolute top-4 right-4 flex flex-col gap-2 z-[400]">
        <div className="flex items-center bg-slate-900/90 backdrop-blur-md rounded-lg border border-slate-700 shadow-xl overflow-hidden">
            <button 
              onClick={handlePrev} 
              disabled={segments.length === 0 || currentIndex === 0} 
              className="p-2 hover:bg-slate-800 text-cyan-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Previous Segment"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button 
              onClick={handleAll} 
              className={`p-2 hover:bg-slate-800 transition-colors border-x border-slate-700 ${!selectedSegment ? 'text-white bg-slate-800' : 'text-slate-400'}`} 
              title="Show All Segments"
            >
              <Layers className="w-5 h-5" />
            </button>
            <button 
              onClick={handleNext} 
              disabled={segments.length === 0 || (currentIndex !== -1 && currentIndex === segments.length - 1)} 
              className="p-2 hover:bg-slate-800 text-cyan-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Next Segment"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
        </div>
      </div>

      {/* Selected Segment Stats & Controls */}
      {selectedSegment && (
         <div className="absolute bottom-4 left-4 bg-slate-900/90 backdrop-blur-md p-4 rounded-xl border-l-4 border-cyan-500 shadow-2xl z-[400] min-w-[220px] animate-in slide-in-from-bottom-4 fade-in duration-300">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-slate-100 font-bold text-sm flex items-center gap-2">
                 <span className="text-cyan-400">Run #{selectedSegment.id}</span>
              </h4>
              <span className="text-[10px] text-slate-500 font-mono bg-slate-800/50 px-1.5 py-0.5 rounded">
                {selectedSegment.stats.startTime.toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}
              </span>
            </div>
            
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 mb-2">
               <div className="flex flex-col">
                  <span className="text-[10px] uppercase text-slate-500 font-bold flex items-center gap-1"><Clock className="w-3 h-3 text-pink-500"/> Time</span>
                  <span className="text-lg font-mono font-medium text-white">{formatDuration(selectedSegment.stats.duration)}</span>
               </div>
               <div className="flex flex-col">
                  <span className="text-[10px] uppercase text-slate-500 font-bold flex items-center gap-1"><MapPin className="w-3 h-3 text-emerald-400"/> Dist</span>
                  <span className="text-lg font-mono font-medium text-white">{(selectedSegment.stats.distance * 1000).toFixed(0)} <span className="text-xs text-slate-500">m</span></span>
               </div>
               <div className="flex flex-col">
                  <span className="text-[10px] uppercase text-slate-500 font-bold flex items-center gap-1"><Zap className="w-3 h-3 text-cyan-400"/> Avg Spd</span>
                  <span className="text-lg font-mono font-medium text-white">{selectedSegment.stats.avgSpeed.toFixed(1)} <span className="text-xs text-slate-500">km/h</span></span>
               </div>
               {selectedSegment.stats.avgHeartRate && (
                   <div className="flex flex-col">
                      <span className="text-[10px] uppercase text-slate-500 font-bold flex items-center gap-1"><Heart className="w-3 h-3 text-red-500"/> Avg HR</span>
                      <span className="text-lg font-mono font-medium text-white">{selectedSegment.stats.avgHeartRate} <span className="text-xs text-slate-500">bpm</span></span>
                   </div>
               )}
            </div>
            
            {/* Collapsible Trim Controls */}
            {onTrimStart && onTrimEnd && onTrimReset && (
               <div className="border-t border-slate-700 pt-2 mt-2">
                 {!showTrimControls ? (
                   <button 
                      onClick={() => setShowTrimControls(true)}
                      className="w-full flex items-center justify-center gap-2 text-xs text-slate-400 hover:text-white hover:bg-slate-800 py-1.5 rounded transition-colors"
                   >
                      <Settings2 className="w-3 h-3" /> Edit / Trim
                   </button>
                 ) : (
                   <div className="flex flex-col gap-2 animate-fade-in">
                      <div className="flex items-center justify-between mb-1">
                         <span className="text-[10px] font-bold text-slate-400 uppercase">Trim Segment</span>
                         <button onClick={() => setShowTrimControls(false)} className="text-slate-500 hover:text-white"><X className="w-3 h-3" /></button>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <button 
                           onClick={() => onTrimStart(selectedSegment.id)}
                           className="flex-1 text-[10px] bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white flex items-center justify-center gap-1 py-1.5 rounded transition-colors border border-slate-700"
                           title="Cut 1 second from start"
                        >
                           <Scissors className="w-3 h-3" /> Start
                        </button>
                        <button 
                           onClick={() => onTrimEnd(selectedSegment.id)}
                           className="flex-1 text-[10px] bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white flex items-center justify-center gap-1 py-1.5 rounded transition-colors border border-slate-700"
                           title="Cut 1 second from end"
                        >
                           End <Scissors className="w-3 h-3 transform scale-x-[-1]" />
                        </button>
                      </div>
                      <button 
                         onClick={() => onTrimReset(selectedSegment.id)}
                         className="w-full text-[10px] text-cyan-500 hover:text-cyan-400 hover:bg-cyan-500/10 flex items-center justify-center gap-1 py-1 rounded transition-colors"
                      >
                         <RotateCcw className="w-3 h-3" /> Reset Edits
                      </button>
                   </div>
                 )}
               </div>
            )}
         </div>
      )}
      
      <div className="absolute top-4 left-14 bg-slate-900/80 px-3 py-1 rounded-full text-xs text-slate-300 border border-slate-700 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none z-[400]">
         Hover for point speed • Click to focus
      </div>
    </div>
  );
};